import Vue, { CreateElement } from 'src/vue'
import { Component, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import draggable from 'vuedraggable'

import { Body2, Subtitle1, Body1, Caption, Subtitle2 } from '@src/elements/typography/typography'
import Station, { DEFAULT_SCREENSAVER } from '@src/data/station'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Form } from '@src/components/form/form'
import { Input } from '@src/elements/input/input'
import { Option } from '@src/elements/select/option'
import { OptionGroup } from '@src/elements/select/option-group'
import { Select } from '@src/elements/select/select'
import { availableEquipment, locales2D, locales3D } from '@src/util/constants'
import { Button } from '@src/elements/button/button'
import { Icon } from '@src/elements/icon/icon'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ExerciseCardConfig } from '@src/components/card/exercise-card/exercise-card-config/exercise-card-config'
import { WorkoutExercise, WorkoutExerciseConfig } from '@src/data/workout-exercise'
import ExerciseSelector from '@src/components/exercise-selector/exercise-selector'
import { Modal } from '@src/elements/modal/modal'
import { Radio } from '@src/elements/input/radio'
import { Checkbox } from '@src/elements/input/checkbox'
import { track } from '@src/util/functions'
import { Screensaver } from '@src/routes/club/screensaver'
import { StationDTO } from '@src/dto/station-dto'
import { ContentBox } from '@src/elements/content-box/content-box'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import QrcodeVue from 'qrcode.vue'
import { ScreensaverDTO } from '@src/dto/screensaver-dto'
import { StationService } from '@src/service/station'

import './arrangement-settings.sass'
import { ExpertiseLevel } from '@src/data/member'

@Component({
  components: {
    draggable: draggable
  },
  computed: {
    selectedExercises () {
      return this.$store.state.new_workout.workoutExercises
    },
    availableExercises () {
      return this.$store.state.availableExercises
    },
    stations: {
      get: function () {
        return this.$store.state.clubStations
      },
      set: function (stations) {
        stations.forEach((station, index) => station.position = index)
        this.$store.state.clubStations = stations
      }
    }
  }
})
export class ArrangementSettings extends Vue {

  private loading: boolean = true

  private stations: Station[]
  private selectedStation: Station | null = null
  private selectedStationIndex: number = 0

  private selectedExercises: WorkoutExercise[]
  private availableExercises: ExerciseResponse[]
  private showAvailableExercises: boolean = false
  private saving: boolean = false

  private dragOptions: any = {
    group: 'stations'
  }

  private selectedScreensaver: ScreensaverDTO | null = null
  private showScreensaverSelector: boolean = false

  private service: StationService = new StationService()

  constructor () {
    super()
    track('/club/arrangement_settings')
  }

  selectStation (station: Station | null, index: number) {
    this.selectedStation = new Station(station)
    this.selectedStationIndex = index
    this.selectedScreensaver = null
  }

  printQR () {
    window.print()
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CLUB')} - ${this.$root.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.TAB_NAME')}`
  }

  selectEquipment (equipmentName) {
    if (this.selectedStation) {
      let index = this.selectedStation.equipment.indexOf(equipmentName)
      if (index >= 0) {
        this.selectedStation.equipment.splice(index, 1)
      } else {
        this.selectedStation.equipment.push(equipmentName)
      }
    }
  }

  showExercises () {
    this.$store.dispatch('addSingleExerciseToWorkout', { exercise: this.getDefaultExercise() ? this.getDefaultExercise()!.exercise : null })
    this.showAvailableExercises = !this.showAvailableExercises
  }

  filterExercises () {
    let availableExercises: ExerciseResponse[] = []
    availableExercises.push(...this.availableExercises.filter(exercise => {
      const commonEquipment = exercise.equipment.filter(equipment => this.selectedStation ? this.selectedStation.equipment.indexOf(equipment) >= 0 : false)
      return commonEquipment.length > 0 || exercise.equipment.length === 0
    }))
    return availableExercises
  }

  getDefaultExercise () {
    if (this.availableExercises.length > 0) {
      for (let i = 0; i < this.availableExercises.length; i++) {
        if (this.availableExercises[i].id === this.selectedStation!.defaultExerciseId) {
          let workoutExercise = new WorkoutExercise()
          workoutExercise.setExercise(this.availableExercises[i])

          let initialExercise = new WorkoutExerciseConfig()
          initialExercise.setDefaultValues(new ExerciseResponse(this.availableExercises[i]))
          workoutExercise.initialExerciseConfig = initialExercise

          return workoutExercise
        }
      }
    }
  }

  // save the position of stations when drag event ends
  applyStationOrderChanges () {
    this.stations.forEach((station: Station) => {
      return this.updateStationPosition(station)
    })
  }

  save () {
    delete (this.selectedStation as any).position
    let formattedStation: any = new StationDTO(this.selectedStation)
    delete formattedStation.screensaverUrl

    if (this.selectedScreensaver && this.selectedScreensaver.content === DEFAULT_SCREENSAVER.content) {
      formattedStation.screensaver = null
    } else if (this.selectedScreensaver && this.selectedScreensaver.content !== this.selectedStation!.getScreensaver().content) {
      formattedStation.screensaver = {
        ...this.selectedScreensaver,
        content: this.selectedScreensaver.content.includes(',') ? this.selectedScreensaver.content.split(',')[1] : this.selectedScreensaver.content
      }
    }

    this.saving = true
    // submit new settings
    return this.service.edit(formattedStation)
      .then(() => {
        this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
        this.saving = false
        return this.loadStations(this.selectedStationIndex)
      })
      .catch(error => console.log('cannot save station ', error, this.selectedStation))
  }

  setScreensaver (screensaver: ScreensaverDTO | null) {
    this.selectedScreensaver = screensaver
    this.showHideScreenSaverSelector()
  }

  changeAudioHintsEnabled (value?: string) {
    if (!this.selectedStation) return

    this.selectedStation.audioHintsEnabled = value === undefined
      ? !this.selectedStation.audioHintsEnabled
      : value === 'true'
  }

  changeVisualHintsEnabled () {
    this.selectedStation!.visualHintsEnabled = !this.selectedStation!.visualHintsEnabled
  }

  changeAllowAnonymousLogin () {
    this.selectedStation!.allowAnonymousLogin = !this.selectedStation!.allowAnonymousLogin
  }

  changeExerciseTimeoutEnabled () {
    this.selectedStation!.exerciseTimeoutEnabled = !this.selectedStation!.exerciseTimeoutEnabled
  }

  showHideScreenSaverSelector () {
    this.showScreensaverSelector = !this.showScreensaverSelector
    this.$store.dispatch('closeToast')
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }

    if (this.stations.length === 0) {
      return <div>

        <ErrorScreen title={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.REQUEST_ERROR')}
                     message={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SUPPORT_REQUEST')} >
        </ErrorScreen>
        {this.renderQr(h)}
      </div>
    }

    if (!this.selectedStation) {
      return
    }

    let stations: any[] = this.stations.map((station: Station, index: number) => {
      return <div
        class={`station ${(this.selectedStation && this.selectedStation.id === station.id ? 'selected' : '')}`}
        onClick={() => {
          this.selectStation(station, index)
        }}>
        <div class='station-names'>
          <Subtitle1>{station.displayName}</Subtitle1>
          <Subtitle2>{station.name}</Subtitle2>
        </div>
        <div>
          <Icon name={`brand/${station.stationType}`} />
          <Caption>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.POSITION')} {station.position + 1}</Caption>
        </div>
      </div>
    })

    let locales = (this.selectedStation.stationType === '3d' ? locales3D : locales2D).map(locale => {
      return <Option type='single-option' value={locale} label={this.$t(`LANGUAGES.${locale}`)}
                     selected={this.selectedStation!.displayLocale === locale} />
    })

    let equipment = availableEquipment.map((equipmentName) => {
      let isSelected = this.selectedStation ? this.selectedStation.equipment.indexOf(equipmentName) >= 0 : /* istanbul ignore next: render returned already without a selected station */ false
      return <div class='eq-wrapper'><Checkbox value={isSelected} change={() => this.selectEquipment(equipmentName)}
                  text={this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${equipmentName.toUpperCase()}`)} /></div>
    })

    let availableExercises = this.filterExercises()
    let defaultExercise = this.getDefaultExercise()

    // Due to dropdowns being inside rows, they are all at the same z-index; this means they overlap. A new z-index will be set, in order, for each row container:
    let zIndex = 4

    return <div class='station-settings-wrapper'>
      <div class='text-headers'>
        <Subtitle1>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.STATION_ARRANGEMENT_SETTINGS_INFO', { number: this.stations.length })}</Subtitle1>
        <Subtitle1>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.FULL_BODY_CIRCUIT')}</Subtitle1>
      </div>
      <div class='row'>
        <div class='box-left'>
          <div class='stations'>
            <draggable class='stations-scrollable' onEnd={this.applyStationOrderChanges} v-model={this.stations}
                       options={{
                         ...this.dragOptions,
                         delay: 2000
                       }}>{stations}</draggable>
          </div>
        </div>
        <div class='box-right'>
          <ContentBox class='station-settings' overflow={'scrollable'}>
            <div slot={'content'}>
              <Form direction={'column'}>

                {this.renderStationSettings(h, locales)}

                <div class='row'>
                  <Subtitle1 class='config-label'>{this.$t('EQUIPMENT')}</Subtitle1>
                </div>
                <div class='row'>
                  <div class='station-equipment'>
                    {equipment}
                  </div>
                </div>

                <div class='row'>
                  <Subtitle1
                    class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.DEFAULT_EXERCISE')}</Subtitle1>
                </div>

                {this.renderExerciseInfo(h, defaultExercise, availableExercises)}

                <div class={`row${this.selectedStation && this.selectedStation.stationType === '2d' && this.$store.state.club.audioHintsAvailable ? ' station-main-settings-with-audio-hints' : ''}`} style={`z-index: ${zIndex--}`}>
                  {this.renderExerciseDifficulty(h)}
                  {this.renderFeedbackVerbosity(h)}
                  {this.renderAudioHintsFor2D(h)}
                </div>

                <div class='row radio-buttons-row'>
                  {/*{this.renderVisualHintsFor2D(h)}*/}

                  {this.renderWorkoutWithoutQRCodeFor3D(h)}

                  {/*{this.renderExerciseTimeoutSettingsFor2D(h)}*/}
                </div>

                {/*{this.renderCongratsMessageFor2D(h)}*/}

                {this.renderDisplaySettingsFor2D(h)}
              </Form>
            </div>

            <div slot={'bottom'}>
              <Button type='elevated' class='apply-btn' label={this.$t('APPLY')} click={this.save} />
            </div>
          </ContentBox>
        </div>
      </div>
      {this.renderQr(h)}
    </div>
  }

  renderStationSettings (h: CreateElement, locales) {
    if (!this.selectedStation) return null

    return <div>
      <div class='row'>
        <Input label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.NAME')} type='text' ref='selected-station-name'
               placeholder={this.selectedStation.name}
               value={this.selectedStation.displayName}
               valueChanged={(value: string) => this.selectedStation!.displayName = value}/>
        <Select label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.STATION_LANGUAGE')} ref='display-locale'
                value={this.$t(`LANGUAGES.${this.selectedStation.displayLocale}`)}
                valueChanged={(value: string) => this.selectedStation!.displayLocale = value}>
          <OptionGroup>
            {locales}
          </OptionGroup>
        </Select>
      </div>

      <div class='row'>
        <Body1 class='body-side-info'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.BODY_SIDE_DETAILS')}</Body1>
      </div>

      <div class='row'>
        <Select label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.EXECUTION_SIDE')} ref='side'
                value={this.selectedStation.side ? this.$t(`BODY_SIDE.${this.selectedStation.side.toUpperCase()}`) : this.$t('BODY_SIDE.ALTERNATING')}
                valueChanged={(value: string) => this.selectedStation!.side = value}>
          <Option type='single-option' value='alternating' label={this.$t('BODY_SIDE.ALTERNATING')}
                  selected={this.selectedStation.side === 'alternating'}/>
          <Option type='single-option' value='right' label={this.$t('BODY_SIDE.RIGHT')}
                  selected={this.selectedStation.side === 'right'}/>
          <Option type='single-option' value='left' label={this.$t('BODY_SIDE.LEFT')}
                  selected={this.selectedStation.side === 'left'}/>
        </Select>
      </div>
    </div>
  }

  renderExerciseInfo (h: CreateElement, defaultExercise, availableExercises) {
    return <div class='row exercise-config-row'>
      <div class='column'>
        {defaultExercise ?
          <ExerciseCardConfig type='default' disabled={true} workoutExercise={defaultExercise}
                              club={this.$store.state.club} index={0}/> :
          <div class='empty-exercise-card'>
            {this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SELECTED_DEFAULT_EXERCISE_NOT_AVAILABLE')}
          </div>
        }
        <Button type='elevated' class='exercises-btn'
                label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SCREEN_SAVER.CHANGE')}
                click={this.showExercises}/>
        {this.showAvailableExercises ?
          <Modal overflow={'visible'} show={true}
                 extraStyles={{ height: '70vh' }}
                 title={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SELECT_DEFAULT_EXERCISE')}
                 class='available-exercises-modal'
                 closeCallback={this.showExercises}>
            <ExerciseSelector selectedExercises={[]} selectCard={true} />
            <div class='btn-row' slot='footer'>
              <Button type='outline' label={this.$t('CANCEL')} click={this.showExercises} />
              <Button class='save-btn' label={this.$t('SAVE')} click={this.saveDefaultExercise} />
            </div>
          </Modal>
          : null}
      </div>
      {availableExercises ?
        <div class='column'>
          <Subtitle1
            class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.AVAILABLE_EXERCISES')}</Subtitle1>
          <Button class='availableCount' type='remove' label={`${availableExercises.length}/${this.availableExercises.length}`} />
        </div> : /* istanbul ignore next: filterExercises always returns a list */ null}
    </div>
  }

  renderExerciseDifficulty (h: CreateElement) {
    if (!this.selectedStation) return null

    return <Select label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.DEFAULT_EXERCISES_DIFFICULTY')}
            ref='level-key'
            value={this.$t(`PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.${this.selectedStation.defaultFeedbackLevelKey.toUpperCase()}`)}
                   valueChanged={(value: string) => this.selectedStation!.defaultFeedbackLevelKey = value}>
      <Option type='single-option' value={ExpertiseLevel.BEGINNER}
              label={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.BEGINNER')}
              selected={this.selectedStation.defaultFeedbackLevelKey === ExpertiseLevel.BEGINNER}/>
      <Option type='single-option' value={ExpertiseLevel.INTERMEDIATE}
              label={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.INTERMEDIATE')}
              selected={this.selectedStation.defaultFeedbackLevelKey === ExpertiseLevel.INTERMEDIATE}/>
      <Option type='single-option' value={ExpertiseLevel.ADVANCED}
              label={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.ADVANCED')}
              selected={this.selectedStation.defaultFeedbackLevelKey === ExpertiseLevel.ADVANCED}/>
    </Select>
  }

  renderFeedbackVerbosity (h: CreateElement) {
    if (!this.selectedStation) return null

    return <Select label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.FEEDBACK_DURING_EXERCISE')} ref='verbosity'
            value={this.selectedStation.defaultFeedbackVerbosity ? this.$t(`PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_${this.selectedStation.defaultFeedbackVerbosity.toUpperCase()}`) : ''}
                   valueChanged={(value: string) => this.selectedStation!.defaultFeedbackVerbosity = value}>
      <Option type='single-option' value='full'
              label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_FULL')}
              selected={this.selectedStation.defaultFeedbackVerbosity === 'full'} />
      <Option type='single-option' value='none'
              label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_NONE')}
              selected={this.selectedStation.defaultFeedbackVerbosity === 'none'} />
    </Select>
  }

  renderAudioHintsFor2D (h: CreateElement) {
    if (!this.selectedStation || this.selectedStation.stationType !== '2d' || !this.$store.state.club.audioHintsAvailable) return null

    return <Select label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.AUDIO_HINTS')} ref='audioHints'
                   value={this.selectedStation.audioHintsEnabled ? this.$t('ENABLE') : this.$t('DISABLE')}
                   valueChanged={this.changeAudioHintsEnabled}>
      <Option type='single-option' value='true' label={this.$t('ENABLE')}
              selected={this.selectedStation.audioHintsEnabled} />
      <Option type='single-option' value='false' label={this.$t('DISABLE')}
              selected={!this.selectedStation.audioHintsEnabled} />
    </Select>
  }

  renderVisualHintsFor2D (h: CreateElement) {
    if (!this.selectedStation || this.selectedStation.stationType !== '2d') return null

    return <div class='column'>
      <Subtitle1 class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.VISUAL_HINTS')}</Subtitle1>
      <el-radio-group>
        <Radio value={'true'} text={this.$t('ENABLE')} bindedProp={`${this.selectedStation.visualHintsEnabled}`}
               change={this.changeVisualHintsEnabled} ref='enableVisualHint'/>
        <Radio value={'false'} text={this.$t('DISABLE')} bindedProp={`${this.selectedStation.visualHintsEnabled}`}
               change={this.changeVisualHintsEnabled} ref='disableVisualHint'/>
      </el-radio-group>
    </div>
  }

  renderWorkoutWithoutQRCodeFor3D (h: CreateElement) {
    if (!this.selectedStation || this.selectedStation.stationType !== '3d') return null

    return <div class='column'>
      <Subtitle1
        class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.WORKOUT_WITHOUT_QR')}</Subtitle1>
      <el-radio-group>
        <Radio value={'true'} text={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.ALLOW')}
               bindedProp={`${this.selectedStation.allowAnonymousLogin}`}
               change={this.changeAllowAnonymousLogin} ref='allowQR'/>
        <Radio value={'false'} text={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.DO_NOT_ALLOW')}
               bindedProp={`${this.selectedStation.allowAnonymousLogin}`}
               change={this.changeAllowAnonymousLogin} ref='doNotAllowQR'/>
      </el-radio-group>
    </div>
  }

  renderExerciseTimeoutSettingsFor2D (h: CreateElement) {
    if (!this.selectedStation || this.selectedStation.stationType !== '2d') return null

    return <div class='column'>
      <Subtitle1 class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.EXERCISE_TIMEOUT')}</Subtitle1>
      <el-radio-group>
        <Radio value={'true'} text={this.$t('ENABLE')} bindedProp={`${this.selectedStation.exerciseTimeoutEnabled}`}
               change={this.changeExerciseTimeoutEnabled} ref='enableTimeout'/>
        <Radio value={'false'} text={this.$t('DISABLE')} bindedProp={`${this.selectedStation.exerciseTimeoutEnabled}`}
               change={this.changeExerciseTimeoutEnabled} ref='disableTimeout'/>
      </el-radio-group>
    </div>
  }

  renderCongratsMessageFor2D (h: CreateElement) {
    if (!this.selectedStation || this.selectedStation.stationType !== '2d') return null

    return <div class='row'>
      <Input label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.CONGRATS_MESSAGE')} type='text'
             value={this.selectedStation.congratulationMessage}
             valueChanged={(value: string) => this.selectedStation!.congratulationMessage = value} ref='congratsMsg'/>
    </div>
  }

  renderDisplaySettingsFor2D (h: CreateElement) {
    if (!this.selectedStation || this.selectedStation.stationType !== '2d') { return null }

    return <div class='row'>
        <div class='column screensaver-wrapper'>
          <Subtitle1
            class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SCREEN_SAVER.STATION_SCREENSAVER')}</Subtitle1>
          {this.renderScreenSaver(h)}
          {this.showScreensaverSelector ?
            <Screensaver initial={this.selectedStation!.getScreensaver()}
                         selectedForUpload={this.selectedScreensaver}
                         saveCallback={this.setScreensaver}
                         closeCallback={this.showHideScreenSaverSelector} /> : null}
        </div>
        <div class='video-display-location'>
          <el-radio-group>
            <Subtitle1
              class='config-label'>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.TRAINER_VIDEO_DISPLAY_LOCATION')}</Subtitle1>
            <Radio value={'top'}
                   text={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.TOP')}
                   bindedProp={this.selectedStation.videoDisplayLocation}
                   change={(e) => this.switchTrainerVideoPosition(e)} ref='switchTrainerVideoPosition' />
            <Radio value={'bottom'}
                   text={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.BOTTOM')}
                   bindedProp={this.selectedStation.videoDisplayLocation}
                   change={(e) => this.switchTrainerVideoPosition(e)} ref='switchTrainerVideoPosition' />
          </el-radio-group>
        </div>
      </div>
  }

  renderQr (h: CreateElement) {
    return <div class='qr-wrapper' ref='qr-wrapper'>
      <Subtitle1>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.STATION_REGISTRATION_QR')}</Subtitle1>
      <Body2>{this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.STATION_REGISTRATION_QR_INFO')}</Body2>
      <QrcodeVue value={`pixRegisterToClub_${this.$store.state.club.id}`} size={250} class='generated-qr-code' />
    </div>
  }

  renderScreenSaver (h: CreateElement) {
    if (!this.selectedStation) {
      return null
    }

    const screensaver = this.selectedScreensaver ? this.selectedScreensaver : this.selectedStation.getScreensaver()

    return <div class='current-screensaver'>
      {this.saving ? <Loader /> : null}
      {screensaver.content_type.includes('video') ?
        <video class='screensaver' muted src={screensaver.content} />
        : <img class='screensaver' src={screensaver.content} />}
      <img class='station-border' src={'/assets/icon/brand/station-border.png'} />
      <Button class='change-btn' label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SCREEN_SAVER.CHANGE')}
              click={this.showHideScreenSaverSelector}
              disabled={this.saving} />
    </div>
  }

  mounted () {
    return this.loadStations(0)
  }

  beforeDestroy () {
    this.$store.dispatch('removeNewWorkout')
    this.$store.dispatch('closeToast')
  }

  @Watch('stations')
  updatePositions (newStations: Station[], oldStations: Station[]) {
    if (!oldStations || !newStations) {
      return
    }
    this.stations = newStations

    newStations.forEach(station => {
      let oldStation = oldStations.find(oldStation => oldStation.id === station.id)
      if (oldStation && oldStation.position !== station.position) {
        return this.updateStationPosition(station)
      }
    })
  }

  updateStationPosition (station) {
    if (station.id === this.selectedStation!.id) this.selectedStationIndex = station.position
    return this.service.edit({ id: station.id, position: station.position, equipment: station.equipment })
  }

  private saveDefaultExercise () {
    this.selectedStation!.defaultExerciseId = this.selectedExercises[this.selectedExercises.length - 1].exerciseId
    this.showExercises()
  }

  private async loadStations (selectedStationIndex: number) {
    let response = await this.service.getAll()
    if (response) {
      let stations = response.sort((a, b) => a.position - b.position).map((attrs) => new Station(camelcaseKeys(attrs)))
      this.selectStation(stations ? stations[selectedStationIndex] : /* istanbul ignore next: map always returns a list */ null, selectedStationIndex)
      this.$store.dispatch('setClubStations', stations)
    }
    this.loading = false
  }

  private switchTrainerVideoPosition (e) {
    if (e) this.selectedStation!.videoDisplayLocation = e
  }
}
